import {
  isArchived,
  legalDescription,
  legalDocMeta,
  legalVersions,
  pathFor,
  resolveLegalText,
  type LegalPageId,
  docKeyFor,
  type Locale,
} from '@nexa/content';
import { Container } from '@nexa/ui';
import { useCopy, useLocale } from '@/core/i18n';
import { PageHead } from '@/core/seo';
import { VersionSwitcher } from './VersionSwitcher.tsx';

export interface LegalDocPageProps {
  readonly pageId: LegalPageId;
  /**
   * HTML thân văn bản của ĐÚNG MỘT locale — nạp bằng `?raw` nên bundler nhúng NGUYÊN VĂN.
   *
   * Trước vé #18 đây là `Record<Locale, string>`, tức mỗi trang mang CẢ HAI bản. Hai `?raw`
   * hằng trong cùng một module thì bundler buộc phải gộp chunk, nên `/vi/privacy/` tải luôn
   * 75.965 B bản `en` mà không bao giờ render.
   */
  readonly html: string;
  /**
   * Locale mà `html` thuộc về. KHÔNG dùng để render — `useLocale()` mới là nguồn sự thật.
   * Nó tồn tại để CHỐT rằng bảng `PAGE_MODULE` nối đúng wrapper vào đúng route: 14 wrapper
   * nối tay, và nối lệch thì trang render văn bản pháp lý SAI NGÔN NGỮ trong im lặng —
   * `resolveLegalText` vẫn chạy êm vì `LEGAL_VALUES` dùng chung.
   */
  readonly htmlLocale: Locale;
}

/**
 * MỘT bộ render cho cả 7 trang pháp lý. Tương ứng `main/src/lib/components/LegalPage.svelte`.
 *
 * ── Vì sao `dangerouslySetInnerHTML` chứ không chuyển sang JSX ──────────────────────────────
 * 316 KB HTML ngữ nghĩa của văn bản ĐÃ ĐĂNG KÝ PHÁP LÝ. Chuyển tay sang JSX là 316 KB cơ hội
 * đánh rơi một ký tự, và prettier sẽ định dạng lại kết quả (`printWidth: 100`) nên diff không
 * bao giờ ổn định. Nhúng nguyên văn thì tính chính xác thành CẤU TRÚC, không phải lời hứa:
 * `.html` trong `packages/content/src/legal/` byte-identical với bằng chứng đã trích từ `main`,
 * và `script/check-legal-text.mjs` canh cả hai chặng.
 *
 * An toàn: nội dung là FIRST-PARTY, commit trong repo, không có đường nào cho input người dùng
 * lọt vào. Đây không phải render HTML từ nguồn không tin được.
 *
 * ── `data-legal-body` ─────────────────────────────────────────────────────────────────────────
 * Mốc cho cổng so văn bản. Nó phải bọc ĐÚNG thân văn bản — h1, ngày, bộ chuyển phiên bản đều
 * nằm NGOÀI, vì bên kỳ vọng của cổng là thân đã trích chứ không gồm những thứ đó.
 */
export default function LegalDocPage({ pageId, html, htmlLocale }: LegalDocPageProps) {
  const locale = useLocale();

  // Nối lệch bảng route là lỗi văn bản pháp lý, không phải lỗi hiển thị — nên ĐỎ TO, đừng render
  // tiếp. Render tiếp nghĩa là phục vụ văn bản sai ngôn ngữ trên một URL đã đăng ký pháp lý.
  if (htmlLocale !== locale) {
    throw new Error(
      `[legal] ${pageId}: wrapper mang html locale "${htmlLocale}" nhưng route đang ở "${locale}" — PAGE_MODULE nối lệch.`,
    );
  }
  const copy = useCopy();
  const meta = legalDocMeta(pageId, locale);
  const versions = legalVersions(pageId, locale);
  const archived = isArchived(pageId, locale);

  return (
    <>
      {/*
        NGOẠI LỆ ĐÃ KÝ của contract §1.3 — user chốt 2026-08-20 (vé #12). Quyết định nằm ở
        `docs/rules/contract.md` §1.3, mục "NGOẠI LỆ ĐÃ KÝ"; đây chỉ nhắc lại LÝ DO. Trước bản vá
        này, khối comment nêu lý do mà KHÔNG nêu quyết định — nên §1.3 lệch mà không có chữ ký nào,
        trong khi ngoại lệ `dangerouslySetInnerHTML` của cùng lô thì có. Reviewer kiến trúc bắt
        được đúng chỗ lệch đó.

        Dùng `PageHead` trực tiếp thay cho `PageMeta` vì `PageMeta` không nhận `noindex`, mà bản
        LƯU TRỮ bắt buộc phải có nó. `buildHeadTags` khi thấy `noindex` sẽ trả `robots:
        "noindex, follow"` VÀ bỏ canonical + hreflang — khớp đúng
        `main/src/lib/components/LegalPage.svelte:33`. Đi kèm: trang pháp lý không có breadcrumb
        JSON-LD, và đó cũng là hành vi của `main` (trang pháp lý ở `main` không render `Seo`).

        Nếu `PageMeta` sau này nhận được CẢ `noindex` VÀ một cờ tắt breadcrumb thì việc đúng là GỠ
        ngoại lệ này, không phải nới nó.
      */}
      <PageHead
        pageId={pageId}
        title={copy.seo[pageId].title}
        description={legalDescription(pageId, locale)}
        {...(archived ? { noindex: true } : {})}
      />

      {/*
        Bề rộng đi qua `Container` biến thể văn-bản-dài (token `--container-prose`), không phải một
        số trong CSS — luật 1 của design system. `RootLayout` chỉ cấp một thẻ `main` co giãn, không
        có container nào, nên nếu đây không bọc thì 316 KB chữ chạy sát mép viewport.
      */}
      <Container width="prose">
        <article className="legal-doc">
          <h1>{copy.page[pageId].heading}</h1>

          {meta.showDates ? (
            <p className="legal-doc__dates">
              {copy.legal.effectiveDate}: {meta.effectiveDate} · {copy.legal.lastUpdated}:{' '}
              {meta.lastUpdated}
            </p>
          ) : null}

          {versions !== undefined && meta.activeVersion !== undefined ? (
            <VersionSwitcher
              versions={versions}
              active={meta.activeVersion}
              hrefFor={(id) => pathFor(id, locale)}
            />
          ) : null}

          <div
            data-legal-body
            className="legal-doc__body"
            dangerouslySetInnerHTML={{
              __html: resolveLegalText(html, docKeyFor(pageId, locale)),
            }}
          />
        </article>
      </Container>
    </>
  );
}
